import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmModal,
  getStatusVariant,
} from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import {
  useGateway,
  useDeleteGateway,
  useTestGateway,
  useGatewayTools,
  useSyncGateway,
} from '@/hooks/useGateways';
import { useSpaceId } from '@/contexts/SpaceContext';
import { routes } from '@/lib/routes';
import type { GatewayTestResult, GatewayTool, GatewaySyncResult } from '@/types';
import CreateGatewayModal from './CreateGatewayModal';

export default function GatewayDetail() {
  const { gatewayId } = useParams<{ gatewayId: string }>();
  const navigate = useNavigate();
  const spaceId = useSpaceId();
  const { success, error: showError } = useToast();

  const { data: gateway, isLoading } = useGateway(gatewayId);
  const deleteGateway = useDeleteGateway();
  const testGateway = useTestGateway();
  const {
    data: remoteTools,
    isLoading: toolsLoading,
    error: toolsError,
    refetch: refetchTools,
  } = useGatewayTools(gatewayId);
  const syncGateway = useSyncGateway();

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [testResult, setTestResult] = useState<GatewayTestResult | null>(null);
  const [syncResult, setSyncResult] = useState<GatewaySyncResult | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!gateway) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Gateway not found.{' '}
        <Link to={routes.console.spaceToolsGateways(spaceId)} className="text-primary underline">
          Back to gateways
        </Link>
      </div>
    );
  }

  const handleTest = async () => {
    setTestResult(null);
    try {
      const result = await testGateway.mutateAsync(gateway.gatewayId);
      setTestResult(result);
      if (result.success) {
        success(`Connection successful — ${result.toolCount ?? 0} tools available`);
      } else {
        showError(result.error || 'Connection test failed');
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Connection test failed');
    }
  };

  const handleSync = async () => {
    setSyncResult(null);
    try {
      const result = await syncGateway.mutateAsync(gateway.gatewayId);
      setSyncResult(result);
      success(
        `Sync complete: ${result.imported} imported, ${result.updated} updated, ${result.removed} removed`
      );
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Sync failed');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteGateway.mutateAsync(gateway.gatewayId);
      success('Gateway deleted');
      navigate(routes.console.spaceToolsGateways(spaceId));
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete gateway');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
            <Link to={routes.console.spaceToolsGateways(spaceId)} className="hover:text-foreground">
              Gateways
            </Link>
            <span>/</span>
          </div>
          <h1 className="text-2xl font-bold text-foreground">{gateway.name}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={gateway.enabled ? getStatusVariant(gateway.status || 'active') : 'gray'}>
            {gateway.enabled ? (gateway.status || 'active') : 'disabled'}
          </Badge>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Configuration */}
        <Card>
          <CardHeader title="Configuration" action={<Button variant="outline" size="sm" onClick={() => setEditModalOpen(true)}>Edit</Button>} />
          <dl className="space-y-3">
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Endpoint URL</dt>
              <dd className="mt-0.5 text-sm text-foreground break-all">{gateway.endpoint}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Region</dt>
              <dd className="mt-0.5 text-sm text-foreground">{gateway.region || '-'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Default Tool Group</dt>
              <dd className="mt-0.5 text-sm text-foreground">{gateway.defaultGroup || '-'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Enabled</dt>
              <dd className="mt-0.5 text-sm text-foreground">{gateway.enabled ? 'Yes' : 'No'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Gateway ID</dt>
              <dd className="mt-0.5 text-sm text-foreground font-mono text-xs">{gateway.gatewayId}</dd>
            </div>
          </dl>
        </Card>

        {/* Connectivity Test */}
        <Card>
          <CardHeader title="Connectivity Test" />
          <div className="space-y-4">
            <Button
              onClick={handleTest}
              disabled={testGateway.isPending || !gateway.enabled}
              variant="outline"
            >
              {testGateway.isPending ? 'Testing...' : 'Test Connection'}
            </Button>
            {!gateway.enabled && (
              <p className="text-sm text-muted-foreground">Enable the gateway before testing.</p>
            )}
            {testResult && (
              <div
                className={`rounded-md p-3 text-sm ${
                  testResult.success
                    ? 'bg-green-50 border border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300'
                    : 'bg-red-50 border border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300'
                }`}
              >
                {testResult.success ? (
                  <div className="space-y-1">
                    <p className="font-medium">Connection successful</p>
                    {testResult.toolCount != null && <p>Tools available: {testResult.toolCount}</p>}
                    {testResult.latencyMs != null && <p>Latency: {testResult.latencyMs}ms</p>}
                    {testResult.serverInfo && (
                      <p>
                        Server: {testResult.serverInfo.name} v{testResult.serverInfo.version}
                      </p>
                    )}
                  </div>
                ) : (
                  <div>
                    <p className="font-medium">Connection failed</p>
                    {testResult.error && <p className="mt-1">{testResult.error}</p>}
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Tools Panel */}
      <Card>
        <CardHeader
          title="Tools"
          description="Preview available tools from this gateway and sync them into your org"
          action={
            <Button
              onClick={handleSync}
              disabled={syncGateway.isPending || !gateway.enabled}
            >
              {syncGateway.isPending ? 'Syncing...' : 'Sync Tools'}
            </Button>
          }
        />

        {syncResult && (
          <div className="mb-4 rounded-md p-3 text-sm bg-blue-50 border border-blue-200 text-blue-800 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-300">
            Last sync: {syncResult.imported} imported, {syncResult.updated} updated,{' '}
            {syncResult.removed} removed
          </div>
        )}

        {toolsLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : toolsError ? (
          <div className="rounded-md p-3 text-sm bg-red-50 border border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
            <p className="font-medium">Failed to load tools from gateway</p>
            <p className="mt-1">
              {toolsError instanceof Error ? toolsError.message : 'Check endpoint URL and credentials.'}
            </p>
            <Button variant="outline" size="sm" className="mt-2" onClick={() => refetchTools()}>
              Retry
            </Button>
          </div>
        ) : !remoteTools || remoteTools.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">
            {gateway.enabled
              ? 'No tools found. Run a connectivity test to verify the endpoint.'
              : 'Enable the gateway to preview tools.'}
          </p>
        ) : (
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                    Name
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                    Description
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {(remoteTools as GatewayTool[]).map((tool) => (
                  <tr key={tool.name} className="hover:bg-muted/30">
                    <td className="px-4 py-2 text-sm font-medium text-foreground whitespace-nowrap">
                      {tool.name}
                    </td>
                    <td className="px-4 py-2 text-sm text-muted-foreground line-clamp-2">
                      {tool.description}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Danger Zone */}
      <Card>
        <CardHeader
          title="Danger Zone"
          description="Irreversible actions"
        />
        <Button
          variant="destructive"
          onClick={() => setDeleteModalOpen(true)}
        >
          Delete Gateway
        </Button>
      </Card>

      {/* Edit Modal */}
      <CreateGatewayModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        gateway={gateway}
      />

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title="Delete Gateway"
        message={`Are you sure you want to delete "${gateway.name}"? This will remove ${gateway.toolCount} synced tools and unassign them from any agents using them.`}
        confirmText="Delete"
        isLoading={deleteGateway.isPending}
      />
    </div>
  );
}
